import { call, put, takeLatest, select, spawn } from 'redux-saga/effects';
import { IAppState } from '@/app-state';
import * as UserHelpers from '@/app/common/user-helpers';
import * as Actions from './actions';
import * as Apis from './apis';
import { DEFAULT_PAGE_SIZE, poBuckets } from '@/app-constants';
import { getChangeRequestReasonTypes } from '@/app/common/reference-data/reference-data-actions';
import { poListFilterAction } from '../list/actions';
import { CHANGE_REQUESTS_LIST_ACTIONS } from './components/change-requests/change-requests-actions';
import { IPutPoConversationFollowup, IDeletePoConversationFollowup } from './interfaces/api-model';

export const poHasUnreadMessages = ({ poDetailsState, poDetailsBucketTotalsState }: IAppState): { purchaseOrderId: number, hasUnreadMessages: boolean; } => ({
  purchaseOrderId: poDetailsState.purchaseOrderId,
  hasUnreadMessages: ((poDetailsState.hasUnreadMessages ? 1 : 0) + poDetailsBucketTotalsState.buyerReviewCountWithUnreadMessages + poDetailsBucketTotalsState.supplierReviewCountWithUnreadMessages) > 0
});

export function* getPoDetails(payload) {
  try {
    const request: Apis.IGetPurchaseOrderVerificationDetails = {
      purchaseOrderId: payload.poId
    };
    const response = yield call(Apis.getOrderVerificationDetails, request);
    yield put(Actions.getPoDetailsSuccess(response.purchaseOrderVerificationDetail));
    yield put(getChangeRequestReasonTypes({ customerCode: response.purchaseOrderVerificationDetail.customerCode }));
  } catch (error) {
    yield put(Actions.getPoDetailsFailure(error));
  }
}

export function* getPoDetailsBucketTotals(request) {
  try {
    const response = yield call(Apis.getPoDetailsBucketTotals, request.payload);
    yield put(Actions.getPoDetailsBucketTotalsSuccess(response));
  } catch (error) {
    yield put(Actions.getPoDetailsBucketTotalsFailure(error));
  }
}

//#region getPoItems

export const getSearchText = (state: IAppState) => state.poDetailsState.searchText;

export function* getPoItems(action) {
  try {
    let searchText = action.payload.searchText;

    if (!searchText) {
      searchText = yield select(getSearchText);
    }

    const response = yield call(Apis.getPoItems, {
      ...action.payload,
      searchText
    });

    const poItems = {
      ...response,
      currentPage: action.payload.pageNumber,
      poId: action.payload.poId
    };

    yield put(Actions.getPoItemsSuccess(poItems));
  } catch (error) {
    yield put(Actions.getPoItemsFailure(error));
  }
}

export function* watchGetPoItemsSaga() {
  yield takeLatest(Actions.PO_ITEMS_ACTIONS.REQUEST, getPoItems);
}

//#endregion

export function* getConfirmedPoItems(action) {
  try {

    let searchText = action.payload.searchText;

    if (!searchText) {
      searchText = yield select(getSearchText);
    }

    const response = yield call(Apis.getPoItems, {
      ...action.payload,
      searchText
    });

    const poItems = { ...response, currentPage: action.payload.pageNumber, poId: action.payload.poId };
    yield put(Actions.getConfirmedPoItemsSuccess(poItems));

  } catch (error) {
    yield put(Actions.getConfirmedPoItemsFailure(error));
  }
}

export function* getRevisedPoItems(action) {
  try {

    let searchText = action.payload.searchText;

    if (!searchText) {
      searchText = yield select(getSearchText);
    }

    const response = yield call(Apis.getPoItems, {
      ...action.payload,
      searchText
    });

    const poItems = { ...response, currentPage: action.payload.pageNumber, poId: action.payload.poId };
    yield put(Actions.getPoItemsRevisedSuccess(poItems));

  } catch (error) {
    yield put(Actions.getPoItemsRevisedFailure(error));
  }
}

export function* postApprovePo(request) {
  try {
    const response = yield call(Apis.postApprovePo, request.payload);
    if (request.payload.confirmPo) {
      yield put(Actions.postApprovePoSuccess({
        ...response,
        ...request.payload,
        bucketType: request.payload.bucketType
      }));
      yield put(poListFilterAction(request.payload.poId));
    } else {
      yield put(Actions.approveSelectedItemsSuccess({
        ...response,
        ...request.payload
      }));
    }
  } catch (error) {
    yield put(Actions.postApprovePoFailure(error));
  }
}

//#region searchPoItems

export const getSearchPoItemsEffects = (state: IAppState) => ({
  bucketType: state.poDetailsBucketTotalsState.activeBucketId,
  customerCode: state.poDetailsState.customerCode,
  poId: state.poDetailsState.purchaseOrderId
});

export function* searchPoItems(action) {
  const request = yield select(getSearchPoItemsEffects);

  request.pageNumber = 1;
  request.pageSize = DEFAULT_PAGE_SIZE;
  request.searchText = action.searchText;

  switch (request.bucketType) {
    case poBuckets.new.bucketTypeId:
      yield put(Actions.getPoItemsRequest(request));
      break;

    case poBuckets.confirmed.bucketTypeId:
      yield put(Actions.getConfirmedPoItemsRequest(request));
      break;

    case poBuckets.revised.bucketTypeId:
      yield put(Actions.getPoItemsRevisedRequest(request));
      break;
  }

  const partyCode = UserHelpers.getUserPoPartyCode(request.customerCode, action.userAccessPolicies);

  yield put(Actions.getPoDetailsBucketTotalsRequest({
    partyCode,
    poId: request.poId,
    searchText: request.searchText
  }));
}

export function* watchSearchPoItemsSaga() {
  yield takeLatest(Actions.PO_ITEMS_ACTIONS.SEARCH, searchPoItems);
}

//#endregion

export function* deletePoConversationFollowupSaga(payload) {
  try {
    const request: IDeletePoConversationFollowup = {
      ...payload.request
    };
    yield call(Apis.deletePoConversationFollowup, request);
    yield put(Actions.deletePoConversationFollowupSuccess(payload.request));
  } catch (error) {
    yield put(Actions.conversationFollowupFailureAction(error));
  }
}

export function* putPoConversationFollowupSaga(payload) {
  try {
    const request: IPutPoConversationFollowup = {
      ...payload.request
    };
    yield call(Apis.putPoConversationFollowup, request);
    yield put(Actions.putPoConversationFollowupSuccess(payload.request));
  } catch (error) {
    yield put(Actions.conversationFollowupFailureAction(error));
  }
}

export function* updatePoListUnread() {
  const { purchaseOrderId, hasUnreadMessages } = yield select(poHasUnreadMessages);
  if (!hasUnreadMessages) {
    yield put(Actions.markPoAsRead(purchaseOrderId));
  }
}

export function* watchDeletePoConversationFollowupSaga() {
  yield takeLatest(Actions.PO_DETAILS_ACTIONS.FOLLOWUP_DELETE_REQUEST, deletePoConversationFollowupSaga);
}

export function* watchPutPoConversationFollowupSaga() {
  yield takeLatest(Actions.PO_DETAILS_ACTIONS.FOLLOWUP_PUT_REQUEST, putPoConversationFollowupSaga);
}

export function* watchGetPoDetailsSaga() {
  yield takeLatest(Actions.PO_DETAILS_ACTIONS.REQUEST, getPoDetails);
}

export function* watchGetPoDetailsBucketTotalsSaga() {
  yield takeLatest(Actions.PO_DETAILS_BUCKET_TOTALS_ACTIONS.REQUEST, getPoDetailsBucketTotals);
}

export function* watchGetConfirmedPoItemsSaga() {
  yield takeLatest(Actions.CONFIRMED_PO_ITEMS_ACTIONS.REQUEST, getConfirmedPoItems);
}

export function* watchPostApprovePoSaga() {
  yield takeLatest(Actions.APPROVE_PO_ACTIONS.REQUEST, postApprovePo);
}

export function* watchPoUnreadSaga() {
  yield takeLatest([Actions.PO_DETAILS_ACTIONS.REMOVE_UNREAD, CHANGE_REQUESTS_LIST_ACTIONS.REMOVE_UNREAD], updatePoListUnread);
}

export function* watchGetRevisedPoItemsSaga() {
  yield takeLatest(Actions.POITEMS_REVISED_ACTIONS.REQUEST, getRevisedPoItems);
}
